/**
 * ActionAccordion - Shell wrapper for v2 action cards.
 *
 * Owns the ActionCardShell (checkbox, title, edit button). Content is
 * always visible (no collapse). Manages edit state internally and exposes
 * it via ActionEditContext so content components can read isEditing.
 *
 * Also owns isDirty / isSaving state and routes save/cancel through
 * mutable refs that card-level edit forms populate during render.
 */

import type { ReactNode, RefObject } from 'react';
import { createContext, useContext } from 'react';

import { ConfirmationDialog } from '@/lib/base/overlays';
import { unwrapPropertyValue } from '@/types/crm';
import { humanize } from '@/utils/text';

import type { ProposedAction } from '../../lib/proposals/core/types';
import { isObjectCreatedPayload } from '../../lib/proposals/core/types';
import { useActionDisplayNumber } from '../../lib/proposals/hooks/ActionDisplayNumberContext';
import type { ActionTypeKey } from '../../lib/proposals/registry/types';
import {
  getActionKey,
  isEditableAction,
  lookupAction,
} from '../../lib/proposals/registry/utils';
import { ActionCardShell } from './ActionCardShell';
import type { ActionVariant } from './shared/variants';
import { useActionEditState } from './useActionEditState';

/**
 * Generic human-readable titles by action type key.
 * These are static labels that describe the action type, not the specific payload.
 */
const ACTION_TYPE_TITLES: Record<ActionTypeKey, string> = {
  gmail_send: 'Send email',
  calendar_event_create: 'Create calendar event',
  external_action: 'External action',
  onboarding_action: 'Onboarding',
  'onboarding_action:customize_pipeline': 'Customize pipeline',
  create_pipeline: 'Create pipeline',
  create_validation_rule: 'Create validation rule',
  'onboarding_action:review_permissions': 'Review permissions',
  'timeline_event:ahoy_property_updated': 'Update properties',
  'timeline_event:ahoy_object_created': 'Create item',
  'timeline_event:ahoy_object_deleted': 'Delete item',
  'timeline_event:ahoy_association_added': 'Link items',
  'timeline_event:ahoy_association_removed': 'Unlink items',
  'timeline_event:ahoy_object_merged': 'Merge items',
  'timeline_event:stage_association_added': 'Update stage',
  'timeline_event:stage_association_removed': 'Remove stage',
  'timeline_event:hidden_entity_add': 'Update item',
  ask_user_for_info: 'Fill in fields',
  create_automation: 'Create automation',
};

/** Stable no-op callback to avoid creating a new function on every render */
const NOOP = () => {};

// ─── Edit Context ───────────────────────────────────────────────────────────

export interface ActionEditContextValue {
  isEditing: boolean;
  isDirty: boolean;
  /**
   * Enter edit mode from inside the card content — the click-to-edit path,
   * where clicking a proposed value both opens the editor and arms the
   * shell's Save/Cancel footer. `null` when this action isn't editable, so
   * content can render its values as plain read-only text instead of offering
   * a click target that would do nothing.
   *
   * Editing entered this way is the SAME edit session the pencil button
   * starts: one `isEditing` flag, one dirty state, one save handler.
   */
  startEditing: (() => void) | null;
  /** Card calls this to update dirty state in the accordion */
  setDirty: (dirty: boolean) => void;
  /**
   * Mutable ref that card-level edit forms populate with their save handler.
   * The accordion calls this when the Save button is clicked.
   * Resolving the promise indicates success; rejecting shows an error toast.
   */
  saveHandlerRef: RefObject<(() => Promise<void>) | null>;
  /**
   * Mutable ref that card-level edit forms populate with their cancel/reset handler.
   * The accordion calls this when the user confirms discarding unsaved changes.
   */
  cancelHandlerRef: RefObject<(() => void) | null>;
}

// eslint-disable-next-line react-refresh/only-export-components -- context co-located with provider
export const ActionEditContext = createContext<ActionEditContextValue>({
  isEditing: false,
  isDirty: false,
  startEditing: null,
  setDirty: () => {},
  // Stable fallback refs — never mutated, just satisfy the type
  saveHandlerRef: { current: null },
  cancelHandlerRef: { current: null },
});

/**
 * Read the current edit state from the nearest ActionAccordion.
 * Returns `{ isEditing: false, isDirty: false, ... }` when used outside the context.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with provider
export function useActionEdit(): ActionEditContextValue {
  return useContext(ActionEditContext);
}

// ─── Component ──────────────────────────────────────────────────────────────

interface ActionAccordionProps {
  action: ProposedAction;
  children: ReactNode;
  /** Whether to show selection checkbox (only shown for multi-action proposals) */
  showCheckbox?: boolean;
  /** Whether the action is selected */
  isSelected?: boolean;
  /** Whether the checkbox is disabled (dependency not selected) */
  isCheckboxDisabled?: boolean;
  /** Reason checkbox is disabled (shown in tooltip) */
  disabledReason?: string | null;
  /** Callback when selection changes */
  onSelectionChange?: () => void;
  /** Whether the action is currently being processed (disables editing) */
  isProcessing?: boolean;
  /** Organization ID for entity links */
  orgId?: number;
  /** Optional content for the right side of the header (e.g. Gmail badge, status badge) */
  headerRight?: ReactNode;
  /** Whether to show the execution order number in the title. Default: true. */
  showExecutionOrder?: boolean;
  /**
   * When provided, bypasses the internal ACTION_TYPE_TITLES lookup and uses
   * this string as the card title instead. Useful for merged action groups
   * (e.g. "Create new deal").
   */
  titleOverride?: string;
}

/**
 * Provides the ActionCardShell with checkbox, edit button, and always-visible content.
 * Used by MultiActionTimeline to wrap individual action cards with selection
 * and edit affordances.
 */
export function ActionAccordion({
  action,
  children,
  showCheckbox = false,
  isSelected = true,
  isCheckboxDisabled = false,
  disabledReason = null,
  onSelectionChange,
  isProcessing = false,
  orgId: _orgId,
  headerRight,
  showExecutionOrder = true,
  titleOverride,
}: ActionAccordionProps) {
  // The card's position in the rendered list — NOT `execution_order`, which
  // counts cards the user never sees (hidden no-ops, merged-away associations).
  const displayNumber = useActionDisplayNumber(action.id);

  // Shared edit-state plumbing (also used by MixedActionExpandedRow)

  // Stage association detection uses the hardcoded fallback set.
  // Dynamic detection via target_entity_kind_name is disabled until the
  // backend populates that field on user-facing stage association types.
  // See acceptance criteria "Critical Backend Finding".
  const key = getActionKey(action);

  // Look up registry entry — used for dynamic title (update properties)
  // and to determine if shell-driven editing is supported.
  const entry = lookupAction(action);

  let rawTitle: string;
  if (titleOverride) {
    rawTitle = titleOverride;
  } else if (
    (key === 'timeline_event:ahoy_property_updated' ||
      key === 'timeline_event:stage_association_added' ||
      key === 'timeline_event:stage_association_removed' ||
      key === 'timeline_event:ahoy_association_added' ||
      key === 'timeline_event:ahoy_association_removed' ||
      key === 'timeline_event:ahoy_object_created' ||
      key === 'timeline_event:ahoy_object_deleted' ||
      key === 'timeline_event:ahoy_object_merged' ||
      key === 'external_action' ||
      key === 'ask_user_for_info') &&
    entry
  ) {
    rawTitle = entry.getTitle(action.payload, action);
  } else if (key) {
    rawTitle = ACTION_TYPE_TITLES[key];
  } else {
    rawTitle = humanize(action.proposed_action_type_name);
  }
  const actionTitle = rawTitle;
  // `isEditableAction` encapsulates the static per-action gating (registry
  // supportsEditing + gmail_send / risk-entity / hidden-entity-stage-only
  // exclusions). Only `isProcessing` remains as a runtime concern layered here.
  const showEditButton = isEditableAction(action) && !isProcessing;

  const {
    editContextValue,
    isEditing,
    activelyEditing,
    isDirty,
    isSaving,
    showDiscardConfirm,
    handleToggleEdit,
    handleSave,
    handleCancel,
    performDiscard,
    handleKeepEditing,
  } = useActionEditState({ actionId: action.id, showEditButton });

  // Derive card variant from registry colorScheme + entity-specific overrides.
  // Risks use warning (yellow) by default, escalating to destructive (red) for high severity.
  let cardVariant: ActionVariant =
    (entry?.colorScheme as ActionVariant) ?? 'default';
  if (
    cardVariant === 'default' &&
    isObjectCreatedPayload(action.payload) &&
    action.payload.entity_type_name === 'ahoy_risk'
  ) {
    const severity = (
      action.payload.payload?.properties as Record<string, unknown> | undefined
    )?.ahoy_severity;
    const severityStr =
      typeof severity === 'string'
        ? severity
        : String(unwrapPropertyValue(severity) ?? '');
    cardVariant = severityStr === 'high' ? 'destructive' : 'warning';
  }

  return (
    <ActionEditContext value={editContextValue}>
      <ActionCardShell
        variant={cardVariant}
        title={actionTitle}
        executionOrder={showExecutionOrder ? displayNumber : undefined}
        showCheckbox={showCheckbox}
        isSelected={isSelected}
        onToggleSelection={onSelectionChange ?? NOOP}
        isCheckboxDisabled={isCheckboxDisabled}
        disabledReason={disabledReason ?? undefined}
        showEditButton={showEditButton}
        isEditing={isEditing}
        onToggleEdit={handleToggleEdit}
        headerRight={headerRight}
        isDirty={isDirty}
        isSaving={isSaving}
        onSave={activelyEditing ? handleSave : undefined}
        onCancel={activelyEditing ? handleCancel : undefined}
      >
        {children}
      </ActionCardShell>

      {/* Discard unsaved changes confirmation */}
      <ConfirmationDialog
        isOpen={showDiscardConfirm}
        title='Discard unsaved changes?'
        description='Your changes will be lost.'
        confirmLabel='Discard'
        cancelLabel='Keep editing'
        variant='destructive'
        onConfirm={performDiscard}
        onCancel={handleKeepEditing}
      />
    </ActionEditContext>
  );
}
